import type {
    GnomeEquipmentItemId,
    GnomeEquippedItemIds,
} from "../../schemas/slices/GnomeCustomizationSliceSchema";
import type {
    GnomeEquipmentCategory,
    GnomeEquipmentSelection,
} from "./gnomeSpineEquipment";

export const GNOME_EQUIPMENT_RARITIES = [
    "common",
    "uncommon",
    "rare",
    "epic",
    "legendary",
] as const;

export type GnomeEquipmentRarity = (typeof GNOME_EQUIPMENT_RARITIES)[number];

export interface GnomeEquipmentCatalogItem {
    category: GnomeEquipmentCategory;
    categoryIndex: number;
    id: GnomeEquipmentItemId;
    rarity: GnomeEquipmentRarity;
}

export const GNOME_EQUIPMENT_CATALOG = [
    {
        id: "headwear-006",
        category: "headwear",
        categoryIndex: 1,
        rarity: "epic",
    },
    {
        id: "headwear-010",
        category: "headwear",
        categoryIndex: 2,
        rarity: "legendary",
    },
    {
        id: "beard-001",
        category: "beard",
        categoryIndex: 1,
        rarity: "rare",
    },
    {
        id: "glasses-001",
        category: "glasses",
        categoryIndex: 1,
        rarity: "uncommon",
    },
    {
        id: "shirt-002",
        category: "shirt",
        categoryIndex: 1,
        rarity: "uncommon",
    },
    {
        id: "shirt-003",
        category: "shirt",
        categoryIndex: 2,
        rarity: "rare",
    },
    {
        id: "pants-001",
        category: "pants",
        categoryIndex: 1,
        rarity: "common",
    },
    {
        id: "pants-002",
        category: "pants",
        categoryIndex: 2,
        rarity: "epic",
    },
    {
        id: "footwear-001",
        category: "footwear",
        categoryIndex: 1,
        rarity: "common",
    },
    {
        id: "footwear-002",
        category: "footwear",
        categoryIndex: 2,
        rarity: "legendary",
    },
    {
        id: "headwear-011",
        category: "headwear",
        categoryIndex: 3,
        rarity: "common",
    },
    {
        id: "shirt-005",
        category: "shirt",
        categoryIndex: 3,
        rarity: "common",
    },
    {
        id: "pants-004",
        category: "pants",
        categoryIndex: 3,
        rarity: "common",
    },
    {
        id: "footwear-004",
        category: "footwear",
        categoryIndex: 3,
        rarity: "common",
    },
    {
        id: "headwear-012",
        category: "headwear",
        categoryIndex: 4,
        rarity: "common",
    },
    {
        id: "headwear-013",
        category: "headwear",
        categoryIndex: 5,
        rarity: "common",
    },
    { id: "shirt-006", category: "shirt", categoryIndex: 4, rarity: "common" },
    { id: "shirt-007", category: "shirt", categoryIndex: 5, rarity: "common" },
    { id: "pants-005", category: "pants", categoryIndex: 4, rarity: "common" },
    { id: "pants-006", category: "pants", categoryIndex: 5, rarity: "common" },
    {
        id: "footwear-005",
        category: "footwear",
        categoryIndex: 4,
        rarity: "common",
    },
    {
        id: "footwear-006",
        category: "footwear",
        categoryIndex: 5,
        rarity: "common",
    },
    {
        id: "glasses-002",
        category: "glasses",
        categoryIndex: 2,
        rarity: "common",
    },
    {
        id: "headwear-014",
        category: "headwear",
        categoryIndex: 6,
        rarity: "common",
    },
    { id: "shirt-008", category: "shirt", categoryIndex: 6, rarity: "common" },
    { id: "pants-007", category: "pants", categoryIndex: 6, rarity: "common" },
    {
        id: "headwear-015",
        category: "headwear",
        categoryIndex: 7,
        rarity: "common",
    },
    {
        id: "headwear-016",
        category: "headwear",
        categoryIndex: 8,
        rarity: "common",
    },
    {
        id: "headwear-017",
        category: "headwear",
        categoryIndex: 9,
        rarity: "common",
    },
    { id: "shirt-009", category: "shirt", categoryIndex: 7, rarity: "common" },
    { id: "shirt-010", category: "shirt", categoryIndex: 8, rarity: "common" },
    { id: "shirt-011", category: "shirt", categoryIndex: 9, rarity: "common" },
    { id: "shirt-012", category: "shirt", categoryIndex: 10, rarity: "common" },
    { id: "pants-008", category: "pants", categoryIndex: 7, rarity: "common" },
    { id: "pants-009", category: "pants", categoryIndex: 8, rarity: "common" },
    { id: "pants-010", category: "pants", categoryIndex: 9, rarity: "common" },
    { id: "pants-011", category: "pants", categoryIndex: 10, rarity: "common" },
    {
        id: "footwear-007",
        category: "footwear",
        categoryIndex: 6,
        rarity: "common",
    },
    {
        id: "footwear-008",
        category: "footwear",
        categoryIndex: 7,
        rarity: "common",
    },
    {
        id: "headwear-018",
        category: "headwear",
        categoryIndex: 10,
        rarity: "common",
    },
    { id: "shirt-013", category: "shirt", categoryIndex: 11, rarity: "common" },
    { id: "shirt-014", category: "shirt", categoryIndex: 12, rarity: "common" },
    {
        id: "shirt-015",
        category: "shirt",
        categoryIndex: 13,
        rarity: "legendary",
    },
    {
        id: "shirt-016",
        category: "shirt",
        categoryIndex: 14,
        rarity: "legendary",
    },
    {
        id: "footwear-009",
        category: "footwear",
        categoryIndex: 8,
        rarity: "common",
    },
    {
        id: "glasses-003",
        category: "glasses",
        categoryIndex: 3,
        rarity: "common",
    },
] as const satisfies readonly GnomeEquipmentCatalogItem[];

export const ALL_GNOME_EQUIPMENT_ITEM_IDS = GNOME_EQUIPMENT_CATALOG.map(
    ({ id }) => id,
);

const GNOME_SPINE_SKIN_BY_ITEM_ID = {
    "headwear-018": "headwear/headwear_018",
    "shirt-013": "shirts/shirt_013",
    "shirt-014": "shirts/shirt_014",
    "shirt-015": "shirts/shirt_015",
    "shirt-016": "shirts/shirt_016",
    "footwear-009": "footwear/footwear_009",
    "glasses-003": "glasses/glasses_003",
    "headwear-015": "headwear/headwear_015",
    "headwear-016": "headwear/headwear_016",
    "headwear-017": "headwear/headwear_017",
    "shirt-009": "shirts/shirt_009",
    "shirt-010": "shirts/shirt_010",
    "shirt-011": "shirts/shirt_011",
    "shirt-012": "shirts/shirt_012",
    "pants-008": "pants/pants_008",
    "pants-009": "pants/pants_009",
    "pants-010": "pants/pants_010",
    "pants-011": "pants/pants_011",
    "footwear-007": "footwear/footwear_007",
    "footwear-008": "footwear/footwear_008",
    "headwear-014": "headwear/headwear_014",
    "shirt-008": "shirts/shirt_008",
    "pants-007": "pants/pants_007",
    "glasses-002": "glasses/glasses_002",
    "footwear-006": "footwear/footwear_006",
    "footwear-005": "footwear/footwear_005",
    "pants-006": "pants/pants_006",
    "pants-005": "pants/pants_005",
    "shirt-007": "shirts/shirt_007",
    "shirt-006": "shirts/shirt_006",
    "headwear-013": "headwear/headwear_013",
    "headwear-012": "headwear/headwear_012",
    "headwear-006": "headwear/headwear_006",
    "headwear-010": "headwear/headwear_010",
    "headwear-011": "headwear/headwear_011",
    "beard-001": "beard/beard_001",
    "glasses-001": "glasses/glasses_001",
    "shirt-002": "shirts/shirt_002",
    "shirt-003": "shirts/shirt_003",
    "shirt-005": "shirts/shirt_005",
    "pants-001": "pants/pants_001",
    "pants-002": "pants/pants_002",
    "pants-004": "pants/pants_004",
    "footwear-001": "footwear/footwear_001",
    "footwear-002": "footwear/footwear_002",
    "footwear-004": "footwear/footwear_004",
} as const;

export function gnomeEquipmentSelectionFromItemIds(
    equippedItemIds: Readonly<GnomeEquippedItemIds>,
): GnomeEquipmentSelection {
    return {
        headwear:
            equippedItemIds.headwear === null
                ? null
                : GNOME_SPINE_SKIN_BY_ITEM_ID[equippedItemIds.headwear],
        beard:
            equippedItemIds.beard === null
                ? null
                : GNOME_SPINE_SKIN_BY_ITEM_ID[equippedItemIds.beard],
        glasses:
            equippedItemIds.glasses === null
                ? null
                : GNOME_SPINE_SKIN_BY_ITEM_ID[equippedItemIds.glasses],
        shirt:
            equippedItemIds.shirt === null
                ? null
                : GNOME_SPINE_SKIN_BY_ITEM_ID[equippedItemIds.shirt],
        pants:
            equippedItemIds.pants === null
                ? null
                : GNOME_SPINE_SKIN_BY_ITEM_ID[equippedItemIds.pants],
        footwear:
            equippedItemIds.footwear === null
                ? null
                : GNOME_SPINE_SKIN_BY_ITEM_ID[equippedItemIds.footwear],
    };
}
